import { storyblokEditable, StoryblokComponent } from "@storyblok/react";
import { useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { Helmet } from "react-helmet";

const Carousel = ({ blok }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0);
  const slides = blok.slides || [];

  const handlePrev = () => {
    setDirection(-1);
    setCurrentIndex((prev) => (prev === 0 ? slides.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setDirection(1);
    setCurrentIndex((prev) => (prev === slides.length - 1 ? 0 : prev + 1));
  };

  const currentSlide = slides[currentIndex];

  // Bg colors
  const backgroundColorMap = {
    dark_blue: "#00033E",
    light_blue: "#1371DC",
    white: "#FFFFFF",
  };

  const bgColor = backgroundColorMap[blok.background_color] || "#FFFFFF";

  // Arrow colors
  let arrowColor;
  switch (blok.background_color) {
    case "dark_blue":
    case "light_blue":
      arrowColor = "white";
      break;
    default:
      arrowColor = "#00033E";
  }

  // Animation variants
  const variants = {
    enter: (dir) => ({
      x: dir > 0 ? 100 : -100,
      opacity: 0,
    }),
    center: { x: 0, opacity: 1 },
    exit: (dir) => ({
      x: dir > 0 ? -100 : 100,
      opacity: 0,
    }),
  };

  if (!currentSlide) return null;

  return (
    <section
      {...storyblokEditable(blok)}
      className="
        relative w-full
        flex flex-col justify-center items-center
        overflow-visible md:overflow-hidden
        transition-colors duration-300
        md:max-h-[763px]
      "
      style={{
        backgroundColor: bgColor,
      }}
    >
      {/* Preload all slide images with Helmet */}
      <Helmet>
        {slides.map((slide) =>
          slide.image?.filename ? (
            <link
              key={slide._uid}
              rel="preload"
              as="image"
              href={slide.image.filename}
            />
          ) : null
        )}
      </Helmet>

      {/* Arrows overlayed, vertically centered */}
      {slides.length > 1 && (
        <>
          {/* Left arrow */}
          <button
            onClick={handlePrev}
            className="
              absolute z-20
              flex items-center justify-center
              w-10 h-10 rounded-full
              bg-gray-200/70 hover:bg-gray-200/90
              backdrop-blur-sm transition
              left-4 md:left-6
              top-1/2 -translate-y-1/2
            "
            aria-label="Previous slide"
          >
            <ChevronLeft size={28} color={arrowColor} />
          </button>

          {/* Right arrow */}
          <button
            onClick={handleNext}
            className="
              absolute z-20
              flex items-center justify-center
              w-10 h-10 rounded-full
              bg-gray-200/70 hover:bg-gray-200/90
              backdrop-blur-sm transition
              right-4 md:right-6
              top-1/2 -translate-y-1/2
            "
            aria-label="Next slide"
          >
            <ChevronRight size={28} color={arrowColor} />
          </button>
        </>
      )}

      {/* Animated slide wrapper */}
      <div className="relative flex justify-center items-stretch w-full max-w-[1440px] h-full">
        <AnimatePresence mode="wait" custom={direction}>
          <motion.div
            key={currentSlide._uid}
            custom={direction}
            variants={variants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.6, ease: "easeInOut" }}
            className="w-full h-full"
          >
            <StoryblokComponent blok={currentSlide} />
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
};

export default Carousel;
